import { z } from 'zod'

/**
 * Wraps a data schema with the standard `ApiResponse` envelope used by the
 * GlassPro backend.
 *
 * @example
 *   const measureSchema = z.object({ id: z.number(), name: z.string() })
 *   const measureListResponse = apiResponseSchema(z.array(measureSchema))
 *   // → { success: bool, data?: { id, name }[], message?, meta?, ... }
 *
 * `data` is optional because some success responses only carry `message`
 * (e.g. delete endpoints), and error responses carry `message` only too.
 */
export const apiResponseSchema = <T extends z.ZodTypeAny>(dataSchema: T) =>
  z.object({
    success: z.boolean(),
    data: dataSchema.optional(),
    message: z.string().optional(),
    httpStatus: z.string().optional(),
    meta: z
      .object({
        total: z.number().optional(),
        totalElements: z.number().optional(),
      })
      .catchall(z.unknown())
      .optional(),
    totalCount: z.number().optional(),
    errorsList: z
      .array(z.object({ field: z.string(), message: z.string() }).catchall(z.unknown()))
      .optional(),
  })

/* ─────────────────────────────────────────────────────────────────────────
 * Common reusable schemas
 *
 * These describe the recurring "id + name" shape used by 50+ short-list /
 * select-option endpoints. Each new endpoint should compose its own schema
 * from these primitives, not reinvent them.
 * ──────────────────────────────────────────────────────────────────────── */

/** `{ id, name }` — backend's universal short-list item shape. */
export const idNameSchema = z.object({
  id: z.union([z.number(), z.string()]),
  name: z.string(),
})
export type IdName = z.infer<typeof idNameSchema>

/** `{ id, name }[]` — the body of `/<entity>-id-name` and `/short-list` endpoints. */
export const idNameListSchema = z.array(idNameSchema)

/** `{ id, fullName }` — used by `/v1/users/short-list`. */
export const userShortItemSchema = z.object({
  id: z.union([z.number(), z.string()]),
  fullName: z.string(),
})
export type UserShortItem = z.infer<typeof userShortItemSchema>

/* ─────────────────────────────────────────────────────────────────────────
 * Auth schemas — non-standard envelope
 *
 * `/me` returns `{ success, meta: User }` instead of the standard
 * `{ success, data: T }` envelope, so this file owns the bespoke
 * envelope schema rather than composing with `apiResponseSchema`.
 * ──────────────────────────────────────────────────────────────────────── */

/** User meta block returned by `/me`.
 *
 * Optional string fields use `.nullish()` (= nullable + optional) because the
 * Spring backend serializes unset values as JSON `null` rather than omitting
 * the key — `email: null` is the common case for users without an email. */
export const userMeSchema = z
  .object({
    id: z.union([z.number(), z.string()]),
    email: z.string().nullish(),
    fullname: z.string().nullish(),
    nickname: z.string().nullish(),
    photo: z.object({ path: z.string().nullish() }).catchall(z.unknown()).nullish(),
    roles: z.array(z.string()).nullish(),
    permissions2: z.record(z.string(), z.unknown()).nullish(),
  })
  .catchall(z.unknown())
export type UserMe = z.infer<typeof userMeSchema>

/** `/me` response envelope. */
export const userMeResponseSchema = z
  .object({
    success: z.boolean(),
    meta: userMeSchema,
  })
  .catchall(z.unknown())
export type UserMeResponse = z.infer<typeof userMeResponseSchema>

/**
 * `Record<EnumKey, DisplayLabel>` — every `/<entity>-types` /
 * `/<entity>-status` endpoint returns this shape: a flat object whose keys
 * are the canonical enum codes (e.g. `STAGE_ACTIVE`) and values are the
 * locale-resolved display labels.
 *
 * Strict on both sides — keys and values must be strings. Drift like
 * numeric values or nested objects throws SchemaParseError.
 */
export const enumDataSchema = z.record(z.string(), z.string())
export type EnumData = z.infer<typeof enumDataSchema>

/** Standard paginated meta block. */
export const paginationMetaSchema = z
  .object({
    total: z.number().optional(),
    totalElements: z.number().optional(),
    totalPages: z.number().optional(),
    page: z.number().optional(),
    size: z.number().optional(),
  })
  .catchall(z.unknown())

/**
 * Paginated list envelope: `{ success, data: T[], meta: pagination }`.
 *
 * Composes `apiResponseSchema` with an array `data` shape, narrowing
 * `meta` to the pagination subset. Use this for any `/pageable/list`
 * style endpoint where the response carries both the page contents and
 * the total count.
 *
 * @example
 *   const customerSchema = z.object({ id: z.number(), name: z.string() })
 *   const customersResponse = paginatedListResponseSchema(customerSchema)
 *   // → { success, data: { id, name }[]?, meta: {total?, totalElements?, ...} }
 *
 * For demo / progressive migration, pass a permissive item schema like
 * `z.object({ id: z.union([z.number(), z.string()]) }).catchall(z.unknown())`
 * — it validates the envelope + the universal `id` field while leaving
 * other fields loose. Tighten the item schema per-feature when ready.
 */
export const paginatedListResponseSchema = <T extends z.ZodTypeAny>(itemSchema: T) =>
  z.object({
    success: z.boolean(),
    data: z.array(itemSchema).optional(),
    message: z.string().optional(),
    httpStatus: z.string().optional(),
    meta: paginationMetaSchema.optional(),
    totalCount: z.number().optional(),
    errorsList: z
      .array(z.object({ field: z.string(), message: z.string() }).catchall(z.unknown()))
      .optional(),
  })

/**
 * Permissive item schema for paginated lists where the per-row shape is
 * not yet fully captured. Validates the universal `id` field + accepts
 * any other keys as `unknown`. Use as the bootstrap step before writing
 * a strict per-feature item schema.
 */
export const looseEntityItemSchema = z
  .object({
    id: z.union([z.number(), z.string()]),
  })
  .catchall(z.unknown())
